import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Checkbox, FormControlLabel, Typography, CircularProgress, Snackbar, Alert } from "@mui/material";
import { Icon } from "@iconify/react";

function DNSScan() {
  const [email, setEmail] = useState("");
  const [domain, setDomain] = useState("");
  const [tosChecked, setTosChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const API_ENDPOINT = "https://dev-platform2.portend.io/portend/v1/ft/dnscheck"; // Replace with actual API host
  const siteKey = "6Ld8IcIqAAAAAOSN4Isuy7Udbt8VFzTe-5y0vD-D"; // Your reCAPTCHA site key

  // ✅ Validate form fields
  const validateFields = () => {
    const fieldErrors = {};
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      fieldErrors.email = "Please enter a valid email address.";
    }
    if (!domain || !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) {
      fieldErrors.domain = "Please enter a valid domain (e.g., example.com).";
    }
    if (!tosChecked) {
      fieldErrors.tos = "You must agree to the Terms of Service.";
    }
    setErrors(fieldErrors);

    return Object.keys(fieldErrors).length === 0;
  };

  // ✅ Handle reCAPTCHA Success
  const handleCaptchaSuccess = (token) => {
    setCaptchaToken(token);
    setIsSubmitDisabled(false);
  };

  // ✅ Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    setIsLoading(true);
    setApiResponse(null);

    const payload = {
      email: email.trim(),
      domain: domain.trim(),
      'reCaptchaToken': captchaToken
    };

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setApiResponse({ success: true, message: data.message || "Scan completed successfully!" });
        setToast({ open: true, message: "Scan request submitted successfully!", severity: "success" });
        setFormSubmitted(true);
      } else {
        setApiResponse({ success: false, message: data.error || "Something went wrong. Try again." });
        setToast({ open: true, message: data.error || "Your request was not accepted. Please try again.", severity: "error" });
      }
    } catch (error) {
      setApiResponse({ success: false, message: "Failed to connect to the server. Please try again." });
      setToast({ open: true, message: "Failed to connect to the server.", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Load reCAPTCHA
  useEffect(() => {
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        window.grecaptcha.render("captcha-container", {
          sitekey: siteKey,
          callback: handleCaptchaSuccess,
        });
      });
    }
  }, []);

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" bgcolor="#f9fafb" p={2} sx={{ paddingBottom: "3rem" }}>
      {/* ✅ Page Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ color: "#000" }}>
          <Icon icon="eos-icons:dns" width={45} height={45} style={{ marginRight: "16px", color: "rgb(0, 145, 70)" }} />
          DNS Checker
        </Typography>
        <Typography variant="body1" sx={{ color: "#333", maxWidth: 800, fontSize: "1.2rem" }}>
          This free online tool scans the domain's servers for common DNS issues, providing a report with ways to resolve them.
        </Typography>
      </Box>

      {/* ✅ Form Panel */}
      {!formSubmitted ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <TextField
            fullWidth
            label="Email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />
          <TextField
            fullWidth
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            error={Boolean(errors.domain)}
            helperText={errors.domain}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />
          <FormControlLabel
            control={<Checkbox checked={tosChecked} onChange={(e) => setTosChecked(e.target.checked)} sx={{ color: "rgb(0, 145, 70)", "&.Mui-checked": { color: "rgb(0, 145, 70)" } }} />}
            label={
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                I have read and agree to the{" "}
                <a href="https://portend.ai/terms-and-conditions/" target="_blank" style={{ color: "rgb(0, 145, 70)", textDecoration: "none" }}>
                  Terms of Service
                </a>
                .
              </Typography>
            }
          />
          {errors.tos && (
            <Typography variant="body2" color="error" sx={{ mb: 1, mt: -1, display: "block" }}>
              {errors.tos}
            </Typography>
          )}

          {/* ✅ reCAPTCHA */}
          <div id="captcha-container" style={{ marginBottom: "16px", display: "block", width: "90%" }}></div>

          {/* ✅ Submit Button */}
          <Button type="submit" variant="contained" disabled={isSubmitDisabled || loading} sx={{
            mt: 2, backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)",
            color: isSubmitDisabled ? "#888" : "#fff", padding: "12px 0",
            width: "300px", fontWeight: "bold",
            "&:hover": { backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)" }
          }}>
            {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Submit"}
          </Button>
          {(toast.severity === 'error') && <Typography variant="body2" sx={{ my: 2, fontSize: '1.5rem', background: '#ba1111', color: '#fff', padding: '5px', borderRadius: '5px' }}>{toast.message} <IconButton sx={{ float: 'right', paddingTop: '5px', color: '#fff' }} onClick={() => setToast({})}><Icon icon="uil:times" width="24" height="24" /></IconButton></Typography>}
        </Box>
      ) : (
        <Box component="div" sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <Typography variant="body1" sx={{ color: "#333", textAlign: "center", fontSize: "1.5rem" }}>
            Thank you. Your request has been submitted. You will be informed by email when the results are available.
          </Typography>
        </Box>
      )}

      {/* ✅ Toast Notifications */}
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DNSScan;
